// Talent Pool (Phase 1a) — a searchable, classified pool of candidates with CV
// upload/parse and an automatic acknowledgment on add. Lives as a tab in the
// Recruitment (ATS) view.
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from '@mui/material';
import { Search, Plus, Upload, Download, FileText } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill, WidgetGrid, KPI } from './ui';
import { EmptyState, UserAvatar } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const LEVELS = ['Entry', 'Mid', 'Senior'];
const CATEGORIES = ['Local', 'Global', 'Affiliate'];

function Sel({ value, onChange, options, style }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{ height: 38, borderRadius: 10, border: `1px solid ${AG.n300}`, padding: '0 10px', fontSize: 13, fontFamily: 'inherit', background: '#fff', color: AG.text, ...style }}>
      {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
    </select>
  );
}
const Field = ({ label, children }) => (
  <label style={{ display: 'block', marginBottom: 12 }}>
    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 5 }}>{label}</span>
    {children}
  </label>
);
const BLANK = { name: '', email: '', role: '', level: '', category: '', profession: '', country: '', educationLevel: '', yearsExperience: '', skills: '' };

export default function TalentPool({ showToast }) {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ q: '', level: '', category: '', pooled: '' });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const cvRef = useRef(null);
  const [cvFor, setCvFor] = useState(null);

  const load = () => api.talentPool({ q: filters.q, level: filters.level, category: filters.category, pooled: filters.pooled })
    .then(setRows).catch((e) => { showToast?.(e.message); setRows([]); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.level, filters.category, filters.pooled]);

  const add = async () => {
    if (!form.name || busy) return;
    setBusy(true);
    try {
      await api.addCandidate({ ...form, skills: form.skills });
      showToast?.('Candidate added' + (form.email ? ' · acknowledgment sent' : ''));
      setForm(null);
      await load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const onCvPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !cvFor) return;
    try { await api.uploadCandidateCv(cvFor, file); showToast?.('CV uploaded'); await load(); }
    catch (err) { showToast?.(err.message); } finally { setCvFor(null); }
  };

  if (!rows) return <CenterSpinner />;
  const pooled = rows.filter((c) => !c.jobId).length;
  const withCv = rows.filter((c) => c.hasCv).length;

  return (
    <>
      <input ref={cvRef} type="file" hidden onChange={onCvPick} />
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Talent Pool" sub="Classified candidate database · CV intake" />
        <PrimaryBtn onClick={() => setForm({ ...BLANK })}><Plus size={14} style={{ marginRight: 6 }} /> Add candidate</PrimaryBtn>
      </div>

      <div style={{ marginBottom: 16 }}>
        <WidgetGrid>
          <KPI label="Candidates" value={rows.length} icon={FileText} iconTone="primary" />
          <KPI label="In pool (no vacancy)" value={pooled} icon={FileText} iconTone="info" />
          <KPI label="With CV" value={withCv} icon={Upload} iconTone="success" />
        </WidgetGrid>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: 11, color: AG.n400 }} />
          <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Search name, role, skill…" className="ag-input" style={{ paddingLeft: 32, height: 38 }} />
        </div>
        <Sel value={filters.level} onChange={(v) => setFilters({ ...filters, level: v })} options={[{ value: '', label: 'All levels' }, ...LEVELS.map((l) => ({ value: l, label: l }))]} />
        <Sel value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={[{ value: '', label: 'All categories' }, ...CATEGORIES.map((c) => ({ value: c, label: c }))]} />
        <Sel value={filters.pooled} onChange={(v) => setFilters({ ...filters, pooled: v })} options={[{ value: '', label: 'All' }, { value: '1', label: 'Pool only' }]} />
        <OutlineBtn onClick={load}>Search</OutlineBtn>
      </div>

      {rows.length === 0 ? (
        <Card><EmptyState icon={FileText} title="No candidates" desc="Add a candidate or adjust the filters." /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {rows.map((c) => (
            <div key={c.id} style={{ background: '#fff', border: `1px solid ${AG.n200}`, borderRadius: 14, padding: 16 }}>
              <div className="flex items-center gap-3">
                <UserAvatar name={c.name} size="sm" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14.5, color: AG.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: AG.n500 }}>{c.role}{c.jobId ? '' : ' · pool'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                {c.level && <Pill tone="primary">{c.level}</Pill>}
                {c.category && <Pill tone="info">{c.category}</Pill>}
                {c.country && <Pill tone="neutral">{c.country}</Pill>}
                {c.yearsExperience != null && <Pill tone="neutral">{c.yearsExperience} yrs</Pill>}
              </div>
              {c.skills?.length > 0 && <div style={{ fontSize: 12, color: AG.n500, marginTop: 8 }}>{c.skills.slice(0, 6).join(' · ')}</div>}
              <div className="flex items-center justify-between" style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${AG.divider}` }}>
                <span style={{ fontSize: 12, color: AG.n400 }}>{c.email || 'no email'}</span>
                {c.hasCv ? (
                  <button onClick={() => api.downloadCandidateCv(c.id, c.cvName).catch(() => {})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.primary, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><Download size={13} /> CV</button>
                ) : (
                  <button onClick={() => { setCvFor(c.id); cvRef.current?.click(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n500, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><Upload size={13} /> Add CV</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add candidate</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div className="flex gap-2">
                <div style={{ flex: 1 }}><Field label="Name"><TextField fullWidth size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field></div>
                <div style={{ flex: 1 }}><Field label="Email"><TextField fullWidth size="small" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="(acknowledgment sent)" /></Field></div>
              </div>
              <div className="flex gap-2">
                <div style={{ flex: 1 }}><Field label="Role / target"><TextField fullWidth size="small" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></Field></div>
                <div style={{ flex: 1 }}><Field label="Profession"><TextField fullWidth size="small" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} /></Field></div>
              </div>
              <div className="flex gap-2">
                <div style={{ flex: 1 }}><Field label="Level"><Sel value={form.level} onChange={(v) => setForm({ ...form, level: v })} options={[{ value: '', label: '—' }, ...LEVELS.map((l) => ({ value: l, label: l }))]} style={{ width: '100%' }} /></Field></div>
                <div style={{ flex: 1 }}><Field label="Category"><Sel value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={[{ value: '', label: '—' }, ...CATEGORIES.map((c) => ({ value: c, label: c }))]} style={{ width: '100%' }} /></Field></div>
                <div style={{ flex: 1 }}><Field label="Years exp."><TextField fullWidth size="small" type="number" value={form.yearsExperience} onChange={(e) => setForm({ ...form, yearsExperience: e.target.value })} /></Field></div>
              </div>
              <div className="flex gap-2">
                <div style={{ flex: 1 }}><Field label="Country"><TextField fullWidth size="small" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field></div>
                <div style={{ flex: 1 }}><Field label="Education"><TextField fullWidth size="small" value={form.educationLevel} onChange={(e) => setForm({ ...form, educationLevel: e.target.value })} /></Field></div>
              </div>
              <Field label="Skills (comma-separated)"><TextField fullWidth size="small" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="React, Node, SQL" /></Field>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={add} disabled={busy}>Add</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
}
